import React, { useEffect, useState } from "react";
import { X, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Coach modal: choose which clients see a video (or share with whole roster).
export const AssignVideoModal = ({ video, onClose, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(new Set(video.assigned_client_ids || []));
  const [shareRoster, setShareRoster] = useState(Boolean(video.share_roster));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/coaching/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/videos/${video.id}/assign`, {
        client_ids: [...selected],
        share_roster: shareRoster,
      });
      toast.success(
        shareRoster
          ? "Shared with your whole roster"
          : `Shared with ${r.data.assigned_client_ids.length} client${r.data.assigned_client_ids.length === 1 ? "" : "s"}`,
      );
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sharing");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4" data-testid="assign-video-modal">
      <div className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-md max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-2xl uppercase">Share video</h3>
          <button type="button" onClick={onClose} data-testid="assign-video-close" className="p-1 text-zinc-500 hover:text-blaze" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-4 truncate">{video.title}</p>

        <label className="flex items-center gap-3 border border-obsidian-600 rounded-sm p-3 mb-3 cursor-pointer hover:border-blaze/50">
          <input
            type="checkbox"
            data-testid="assign-share-roster"
            checked={shareRoster}
            onChange={(e) => setShareRoster(e.target.checked)}
            className="accent-[#FF3B30]"
          />
          <span className="text-sm text-zinc-200 inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-blaze" /> Share with my whole roster
          </span>
        </label>

        {!shareRoster && (
          <div className="space-y-1 mb-4 max-h-60 overflow-y-auto" data-testid="assign-client-list">
            {clients.length === 0 && (
              <p className="text-sm text-zinc-500">No clients on your roster yet.</p>
            )}
            {clients.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-2 py-1.5 rounded-sm hover:bg-obsidian-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`assign-client-${c.id}`}
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="accent-[#FF3B30]"
                />
                <span className="text-sm text-zinc-200">{c.first_name || c.email}</span>
                <span className="text-[10px] text-zinc-500 truncate">{c.email}</span>
              </label>
            ))}
          </div>
        )}

        <button
          type="button"
          data-testid="assign-video-save"
          onClick={save}
          disabled={busy}
          className="blaze-btn w-full py-2.5 text-xs disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save sharing"}
        </button>
      </div>
    </div>
  );
};

export default AssignVideoModal;
